import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How do I log my period?',
    answer:
      'Tap any date on the calendar to open the day view, then tap "Start Period" to mark that day as the first day of your period. You can tap subsequent days to extend your period, or tap "End Period" when your period finishes. You can also log flow intensity (light, medium, heavy) for each day.',
  },
  {
    question: 'How do I understand the cycle phases shown in the app?',
    answer:
      'Nova Cycle divides your cycle into four phases: Menstrual phase (the days of your period, shown in red), Follicular phase (the days after your period ends leading up to ovulation), Ovulation window (the estimated fertile window around ovulation), and Luteal phase (the days after ovulation leading up to your next period). These are calculated based on your logged period history and average cycle length.',
  },
  {
    question: 'What symptoms can I track?',
    answer:
      'You can track a wide range of physical and emotional symptoms including cramps, headaches, backache, bloating, breast tenderness, fatigue, nausea, acne, and sleep changes. You can also log mood entries (happy, calm, anxious, irritable, sad, energetic, and more) and free-text notes for anything else you want to record.',
  },
  {
    question: 'How many periods do I need to log before predictions are accurate?',
    answer:
      'Nova Cycle can begin making predictions after just one logged period, but accuracy improves significantly after 3 or more cycles. The app calculates your average cycle length and uses this to predict future dates. The longer your history, the better the predictions account for natural variation.',
  },
  {
    question: 'Do I need to create an account?',
    answer:
      'No. You can use Nova Cycle without signing in. Your data will be stored locally on your device. However, signing in enables cloud sync, which means your data is backed up and accessible if you switch devices or reinstall the app.',
  },
  {
    question: 'How do I sign in?',
    answer:
      'Tap the profile or settings icon and select Sign In. You can sign in with email and password, or use Sign In with Apple for a quick, privacy-focused sign-in (available on iOS and web).',
  },
  {
    question: 'Is my data synced to the cloud?',
    answer:
      'If you are signed in, yes. Your data is automatically synced to AWS servers in the London (eu-west-2) region. This happens in the background whenever you make changes. If you are offline, changes are queued and synced when connectivity is restored. If you are not signed in, your data remains on your device only.',
  },
  {
    question: 'How do I delete my data?',
    answer:
      'Open Settings in the app, tap Delete Account, and confirm the deletion. This permanently removes all your data from both your device and our cloud servers. This action cannot be undone. If you want to delete your local data only, you can sign out and clear the app data through your device settings.',
  },
  {
    question: 'Can I export my data?',
    answer:
      'Premium subscribers can export cycle data as a PDF report. If you need a full data export in a machine-readable format, contact us at support@wolftechnologies.co.uk and we will provide it within 30 days under your data portability rights.',
  },
  {
    question: 'What are the subscription tiers?',
    answer:
      'Nova Cycle offers three tiers: Free (period logging, basic cycle predictions, symptom tracking, mood tracking, calendar view), Cycle Pass at £2.99/month (adds AI-enhanced predictions, cycle comparison, symptom analytics, extended history), and Premium at £7.99/month or £49.99/year (adds trying-to-conceive mode, health reports, partner sharing, smart reminders, PDF export).',
  },
  {
    question: 'How do I subscribe?',
    answer:
      'On iOS, tap Upgrade in the app — subscriptions are processed through Apple In-App Purchase and billed to your Apple ID. On the web, tap Upgrade and you will be directed to a secure Stripe checkout page to enter your payment details.',
  },
  {
    question: 'How do I manage or cancel my subscription?',
    answer:
      'If you subscribed on iOS: open your iPhone Settings, tap your name, then Subscriptions, tap Nova Cycle, and tap Cancel Subscription. If you subscribed on the web: go to Settings in the app and tap Manage Subscription to access the Stripe customer portal. Your access continues until the end of the current billing period.',
  },
  {
    question: 'Will I lose my data if I cancel?',
    answer:
      'No. Cancelling a subscription does not delete your account or data. You will retain access to all free-tier features and your full cycle history. You will lose access to paid features at the end of your billing period.',
  },
  {
    question: 'How do cycle predictions work?',
    answer:
      'Nova Cycle calculates your average cycle length based on the periods you have logged. Using this average, it predicts your next period start date, fertile window, and estimated ovulation day. These predictions are statistical estimates based on your personal data — they are not medical advice and should not be relied upon as a form of contraception.',
  },
  {
    question: 'My predictions seem inaccurate. What should I do?',
    answer:
      'Ensure you have logged at least 3 complete cycles. Check that past period entries are correct (you can edit any date by tapping on it). Consider upgrading to Cycle Pass for AI-enhanced predictions which handle irregular cycles better. If your cycle has recently changed significantly, the app will adjust as you log more data with the new pattern.',
  },
  {
    question: 'Are predictions medical advice?',
    answer:
      'No. Nova Cycle provides informational predictions based on the data you enter. It is not a medical device and its predictions should not be used as a substitute for professional medical advice, diagnosis, or treatment. If you have concerns about your menstrual health, please consult a healthcare professional.',
  },
  {
    question: 'Where is my data stored?',
    answer:
      'All cloud data is stored on Amazon Web Services (AWS) servers in the eu-west-2 (London) region within the United Kingdom. Data is encrypted both at rest (AES-256) and in transit (TLS 1.2+). A local copy is also cached on your device for offline access.',
  },
  {
    question: 'Who can see my data?',
    answer:
      'Only you. Your data is partitioned by your unique user ID in our database. No other user can access it, and our backend enforces this at the API level. We do not sell, share, or provide access to your health data to any third party.',
  },
  {
    question: 'I was charged but do not have Premium access. What do I do?',
    answer:
      'This can occasionally happen if there is a delay in payment confirmation. Close and reopen the app. If on iOS, go to Settings > your name > Subscriptions and verify the subscription is active. If the issue persists, contact us at support@wolftechnologies.co.uk with your email address and approximate date of purchase.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const NovaCycleSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Nova Cycle Support</h1>
          <p className="legal-page__subtitle">
            Get help with Nova Cycle by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all support requests within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Nova Cycle</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/nova-cycle/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default NovaCycleSupport;
